import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from '../../components/ui/dialog';
import { 
    Loader2, Search, Eye, EyeOff, Trash2, RotateCcw, FileText, MapPin, User, Calendar, RefreshCw,
    ShieldCheck, Camera, CheckCircle, XCircle, Star, ExternalLink
} from 'lucide-react';
import { adminAPI } from '../../lib/api';
import { formatDate } from '../../lib/utils';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const getErrorMessage = (err) => {
    try {
        const detail = err?.response?.data?.detail;
        if (typeof detail === 'string') return detail;
        if (Array.isArray(detail)) return detail.map(e => e?.msg || 'Error').join('. ');
        return err?.message || 'An error occurred';
    } catch { return 'An error occurred'; }
};

const AdminReviews = () => {
    const [loading, setLoading] = useState(true);
    const [reviews, setReviews] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [selectedReview, setSelectedReview] = useState(null);
    const [reviewDetails, setReviewDetails] = useState(null);
    const [detailsLoading, setDetailsLoading] = useState(false);
    const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
    const [actionDialog, setActionDialog] = useState({ open: false, action: '', review: null });

    useEffect(() => { loadReviews(); }, []);

    const loadReviews = async () => {
        setLoading(true);
        try {
            const res = await adminAPI.getAllReviews();
            setReviews(res.data || []);
        } catch (err) {
            toast.error(getErrorMessage(err));
        } finally {
            setLoading(false);
        }
    };

    const loadReviewDetails = async (review) => {
        setDetailsLoading(true);
        setDetailsDialogOpen(true);
        try {
            const res = await adminAPI.getReviewDetails(review.id);
            setReviewDetails(res.data);
        } catch (err) {
            toast.error(getErrorMessage(err));
            setDetailsDialogOpen(false);
        } finally {
            setDetailsLoading(false);
        }
    };

    const handleAction = async (action, review) => {
        try {
            if (action === 'approve') {
                await adminAPI.approveReview(review.id);
                toast.success('Review approved');
            } else if (action === 'hide') {
                await adminAPI.hideReview(review.id);
                toast.success('Review hidden');
            } else if (action === 'delete') {
                await adminAPI.deleteReview(review.id);
                toast.success('Review deleted');
            } else if (action === 'restore') {
                await adminAPI.restoreReview(review.id);
                toast.success('Review restored');
            }
            loadReviews();
        } catch (err) {
            toast.error(getErrorMessage(err));
        }
        setActionDialog({ open: false, action: '', review: null });
    };

    const filteredReviews = reviews.filter(r => {
        const matchesSearch = 
            (r.address_street?.toLowerCase() || '').includes(searchQuery.toLowerCase()) ||
            (r.contractor_name?.toLowerCase() || '').includes(searchQuery.toLowerCase());
        const matchesStatus = statusFilter === 'all' || r.status === statusFilter;
        return matchesSearch && matchesStatus;
    });

    if (loading) {
        return (
            <AdminLayout>
                <div className="flex justify-center items-center min-h-[60vh]">
                    <Loader2 className="h-12 w-12 animate-spin text-primary" />
                </div>
            </AdminLayout>
        );
    }

    return (
        <AdminLayout>
            <div className="p-6 lg:p-8 space-y-6">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div>
                        <h1 className="font-heading font-extrabold text-2xl lg:text-3xl text-foreground">Reviews</h1>
                        <p className="text-muted-foreground">Manage all platform reviews</p>
                    </div>
                    <Button onClick={loadReviews} variant="outline" size="sm">
                        <RefreshCw className="h-4 w-4 mr-2" /> Refresh
                    </Button>
                </div>

                {/* Filters */}
                <Card className="bg-card border-border">
                    <CardContent className="p-4">
                        <div className="flex flex-col sm:flex-row gap-4">
                            <div className="relative flex-1">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                <Input
                                    placeholder="Search by address or contractor..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className="pl-9 bg-accent border-border"
                                />
                            </div>
                            <Select value={statusFilter} onValueChange={setStatusFilter}>
                                <SelectTrigger className="w-full sm:w-40 bg-accent border-border">
                                    <SelectValue placeholder="Filter status" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">All Status</SelectItem>
                                    <SelectItem value="approved">Approved</SelectItem>
                                    <SelectItem value="pending">Pending</SelectItem>
                                    <SelectItem value="flagged">Flagged</SelectItem>
                                    <SelectItem value="hidden">Hidden</SelectItem>
                                    <SelectItem value="archived">Archived</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </CardContent>
                </Card>

                {/* Reviews List */}
                <Card className="bg-card border-border">
                    <CardHeader className="pb-3">
                        <CardTitle className="font-heading font-bold text-lg">
                            {filteredReviews.length} Reviews
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-3 max-h-[600px] overflow-y-auto">
                            {filteredReviews.length === 0 ? (
                                <div className="text-center py-8 text-muted-foreground">
                                    <FileText className="h-8 w-8 mx-auto mb-2" />
                                    <p>No reviews found</p>
                                </div>
                            ) : (
                                filteredReviews.map((review) => (
                                    <div key={review.id} className="p-4 bg-accent/30 rounded-lg border border-border">
                                        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2 mb-1">
                                                    <MapPin className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                                                    <p className="font-medium text-foreground truncate">
                                                        {review.address_street || 'Unknown Address'}
                                                    </p>
                                                    <Badge className={
                                                        review.status === 'approved' ? 'bg-emerald-600' :
                                                        review.status === 'pending' ? 'bg-amber-600' :
                                                        review.status === 'flagged' ? 'bg-red-600' :
                                                        review.status === 'hidden' ? 'bg-gray-600' : 'bg-gray-500'
                                                    }>
                                                        {review.status}
                                                    </Badge>
                                                </div>
                                                <div className="flex items-center gap-4 text-xs text-muted-foreground">
                                                    <span className="flex items-center gap-1">
                                                        <User className="h-3 w-3" />
                                                        {review.contractor_name || 'Unknown'}
                                                    </span>
                                                    <span className="flex items-center gap-1">
                                                        <Calendar className="h-3 w-3" />
                                                        {formatDate(review.created_at)}
                                                    </span>
                                                </div>
                                                {review.notes && (
                                                    <p className="text-sm text-muted-foreground mt-2 line-clamp-2">
                                                        "{review.notes}"
                                                    </p>
                                                )}
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <Button size="sm" variant="outline" onClick={() => loadReviewDetails(review)}>
                                                    <Eye className="h-4 w-4" />
                                                </Button>
                                                {review.status !== 'approved' && (
                                                    <Button size="sm" variant="outline" className="text-emerald-500" 
                                                        onClick={() => setActionDialog({ open: true, action: 'approve', review })}>
                                                        Approve
                                                    </Button>
                                                )}
                                                {review.status !== 'hidden' && (
                                                    <Button size="sm" variant="outline" 
                                                        onClick={() => setActionDialog({ open: true, action: 'hide', review })}>
                                                        <EyeOff className="h-4 w-4" />
                                                    </Button>
                                                )}
                                                {review.status === 'hidden' && (
                                                    <Button size="sm" variant="outline"
                                                        onClick={() => setActionDialog({ open: true, action: 'restore', review })}>
                                                        <RotateCcw className="h-4 w-4" />
                                                    </Button>
                                                )}
                                                <Button size="sm" variant="destructive"
                                                    onClick={() => setActionDialog({ open: true, action: 'delete', review })}>
                                                    <Trash2 className="h-4 w-4" />
                                                </Button>
                                            </div>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Action Confirmation Dialog */}
            <AlertDialog open={actionDialog.open} onOpenChange={(open) => !open && setActionDialog({ open: false, action: '', review: null })}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>
                            {actionDialog.action === 'approve' && 'Approve Review'}
                            {actionDialog.action === 'hide' && 'Hide Review'}
                            {actionDialog.action === 'delete' && 'Delete Review'}
                            {actionDialog.action === 'restore' && 'Restore Review'}
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                            {actionDialog.action === 'approve' && 'This will make the review visible and include it in address scoring.'}
                            {actionDialog.action === 'hide' && 'This will hide the review from public view but preserve it in the system.'}
                            {actionDialog.action === 'delete' && 'This will permanently delete the review. This action cannot be undone.'}
                            {actionDialog.action === 'restore' && 'This will restore the review to its previous visible state.'}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={() => handleAction(actionDialog.action, actionDialog.review)}
                            className={actionDialog.action === 'delete' ? 'bg-destructive hover:bg-destructive/90' : ''}>
                            {actionDialog.action === 'approve' && 'Approve'}
                            {actionDialog.action === 'hide' && 'Hide'}
                            {actionDialog.action === 'delete' && 'Delete'}
                            {actionDialog.action === 'restore' && 'Restore'}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Review Details Dialog */}
            <Dialog open={detailsDialogOpen} onOpenChange={setDetailsDialogOpen}>
                <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>Review Details</DialogTitle>
                    </DialogHeader>
                    
                    {detailsLoading ? (
                        <div className="flex justify-center py-8">
                            <Loader2 className="h-8 w-8 animate-spin text-primary" />
                        </div>
                    ) : reviewDetails ? (
                        <div className="space-y-4">
                            {/* Verification Evidence Section */}
                            <div className="p-3 bg-accent/30 rounded-lg border border-border">
                                <div className="flex items-center gap-2 mb-2">
                                    <ShieldCheck className="h-4 w-4 text-primary" />
                                    <h4 className="font-medium text-sm">Verification Evidence</h4>
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    <div className="flex items-center gap-2">
                                        {reviewDetails.verification_evidence?.job_confirmation ? (
                                            <CheckCircle className="h-4 w-4 text-emerald-500" />
                                        ) : (
                                            <XCircle className="h-4 w-4 text-red-500" />
                                        )}
                                        <span className="text-xs text-muted-foreground">Job Confirmed</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        {reviewDetails.verification_evidence?.has_verified_evidence ? (
                                            <CheckCircle className="h-4 w-4 text-emerald-500" />
                                        ) : (
                                            <XCircle className="h-4 w-4 text-muted-foreground" />
                                        )}
                                        <span className="text-xs text-muted-foreground">Has Evidence</span>
                                    </div>
                                </div>
                                {/* Photo Evidence */}
                                {reviewDetails.verification_evidence?.photo_url && (
                                    <div className="mt-3">
                                        <p className="text-xs text-muted-foreground mb-1 flex items-center gap-1">
                                            <Camera className="h-3 w-3" /> Job Photo
                                        </p>
                                        <a 
                                            href={`${BACKEND_URL}${reviewDetails.verification_evidence.photo_url}`} 
                                            target="_blank" 
                                            rel="noopener noreferrer"
                                            className="text-xs text-primary hover:underline flex items-center gap-1"
                                        >
                                            View Photo <ExternalLink className="h-3 w-3" />
                                        </a>
                                    </div>
                                )}
                                {/* Document Evidence */}
                                {reviewDetails.verification_evidence?.document_url && (
                                    <div className="mt-2">
                                        <p className="text-xs text-muted-foreground mb-1 flex items-center gap-1">
                                            <FileText className="h-3 w-3" /> Invoice/Contract (Admin Only)
                                        </p>
                                        <a 
                                            href={`${BACKEND_URL}${reviewDetails.verification_evidence.document_url}`} 
                                            target="_blank" 
                                            rel="noopener noreferrer"
                                            className="text-xs text-primary hover:underline flex items-center gap-1"
                                        >
                                            View Document <ExternalLink className="h-3 w-3" />
                                        </a>
                                    </div>
                                )}
                            </div>

                            {/* Contractor Info */}
                            {reviewDetails.contractor && (
                                <div className="p-3 bg-accent/30 rounded-lg border border-border">
                                    <div className="flex items-center gap-2 mb-2">
                                        <User className="h-4 w-4 text-primary" />
                                        <h4 className="font-medium text-sm">Contractor</h4>
                                    </div>
                                    <div className="grid grid-cols-2 gap-2 text-xs">
                                        <div>
                                            <span className="text-muted-foreground">Company:</span>
                                            <span className="ml-1 text-foreground">{reviewDetails.contractor.company_name}</span>
                                        </div>
                                        <div>
                                            <span className="text-muted-foreground">Trade:</span>
                                            <span className="ml-1 text-foreground">{reviewDetails.contractor.contractor_trade}</span>
                                        </div>
                                        <div>
                                            <span className="text-muted-foreground">Rank:</span>
                                            <span className="ml-1 text-foreground">{reviewDetails.contractor.rank_name} ({reviewDetails.contractor.rank_points} pts)</span>
                                        </div>
                                        <div>
                                            <span className="text-muted-foreground">Approved Reviews:</span>
                                            <span className="ml-1 text-foreground">{reviewDetails.contractor.approved_reviews_count}</span>
                                        </div>
                                        <div>
                                            <span className="text-muted-foreground">Strikes:</span>
                                            <span className={`ml-1 ${reviewDetails.contractor.moderation_strikes > 0 ? 'text-red-500' : 'text-foreground'}`}>
                                                {reviewDetails.contractor.moderation_strikes}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Address Info */}
                            {reviewDetails.address && (
                                <div className="p-3 bg-accent/30 rounded-lg border border-border">
                                    <div className="flex items-center gap-2 mb-2">
                                        <MapPin className="h-4 w-4 text-primary" />
                                        <h4 className="font-medium text-sm">Address</h4>
                                    </div>
                                    <p className="text-xs text-foreground">
                                        {reviewDetails.address.street_address}, {reviewDetails.address.city}
                                        {reviewDetails.address.state && `, ${reviewDetails.address.state}`} {reviewDetails.address.zip_code}
                                    </p>
                                </div>
                            )}

                            {/* Review Content */}
                            {reviewDetails.review && (
                                <div className="p-3 bg-accent/30 rounded-lg border border-border">
                                    <h4 className="font-medium text-sm mb-2">Review Content</h4>
                                    <div className="grid grid-cols-2 gap-2 text-xs mb-3">
                                        <div><span className="text-muted-foreground">Job Type:</span> <span className="text-foreground">{reviewDetails.review.job_type}</span></div>
                                        <div><span className="text-muted-foreground">Size:</span> <span className="text-foreground">{reviewDetails.review.project_size}</span></div>
                                        <div><span className="text-muted-foreground">Status:</span> <Badge className="text-xs">{reviewDetails.review.status}</Badge></div>
                                        <div><span className="text-muted-foreground">Score:</span> <span className="text-foreground">{reviewDetails.review.weighted_review_score?.toFixed(1)}</span></div>
                                    </div>
                                    <div className="flex flex-wrap gap-2 text-xs mb-3">
                                        <span className={`px-2 py-0.5 rounded ${reviewDetails.review.contract_signed ? 'bg-emerald-500/15 text-emerald-600' : 'bg-red-500/15 text-red-600'}`}>
                                            {reviewDetails.review.contract_signed ? 'Contract Signed' : 'No Contract'}
                                        </span>
                                        <span className={`px-2 py-0.5 rounded ${reviewDetails.review.payment_received ? 'bg-emerald-500/15 text-emerald-600' : 'bg-red-500/15 text-red-600'}`}>
                                            {reviewDetails.review.payment_received ? 'Paid' : 'Not Paid'}
                                        </span>
                                        <span className={`px-2 py-0.5 rounded ${reviewDetails.review.would_work_again ? 'bg-emerald-500/15 text-emerald-600' : 'bg-red-500/15 text-red-600'}`}>
                                            {reviewDetails.review.would_work_again ? 'Would Work Again' : 'Would Not Work Again'}
                                        </span>
                                    </div>
                                    {reviewDetails.review.notes && (
                                        <div className="p-2 bg-background/50 rounded text-xs">
                                            <span className="text-muted-foreground">Notes:</span>
                                            <p className="text-foreground mt-1">{reviewDetails.review.notes}</p>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Contractor Review History */}
                            {reviewDetails.contractor_review_history?.length > 1 && (
                                <div className="p-3 bg-accent/30 rounded-lg border border-border">
                                    <h4 className="font-medium text-sm mb-2">Contractor History at this Address</h4>
                                    <div className="space-y-1">
                                        {reviewDetails.contractor_review_history.map((hist, i) => (
                                            <div key={hist.id} className="flex items-center justify-between text-xs">
                                                <span className="text-muted-foreground">{formatDate(hist.created_at)}</span>
                                                <Badge className="text-xs">{hist.status}</Badge>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    ) : (
                        <p className="text-muted-foreground text-center py-4">No details available</p>
                    )}
                </DialogContent>
            </Dialog>
        </AdminLayout>
    );
};

export default AdminReviews;
